import { SOURCE_OPTIONS } from 'api/apartmentProcurement'

import { addedAptCategoryNames, aptCategoryNames } from './aptCategories'

// Category lists for the daily sheet. A row is either At Apartment (apartment /
// "Corporate") — which uses the At Apartment heads by age band — or a regular
// centre line, which uses the general daily-sheet categories. The regular list
// mirrors the team's Google-sheet dropdown (extend as needed).
export const REGULAR_CATEGORIES = [
  'Art & Design', 'Chess', 'Karate', 'Music', 'Phonics', 'Public Speaking',
  'Robotics', 'Explore', 'Explore Prints', 'Pre-school', 'Other prints',
  'Transport', 'party hunters', 'Event', 'HQ', 'Marketing', 'Construction',
  'Center Material', 'BTL'
]
export const AT_APARTMENT_CATEGORIES = [
  '5-12 Art', '5-12 Robotics', '5-12 Public Speaking',
  '3-5 Art', '3-5 STEM', '3-5 Language'
]
// At Apartment lines also get the categories added from Alfred (e.g. "5+ Music",
// 2026-09-17) — those are At Apartment budget heads, never centre ones.
export const categoryOptionsFor = (atApartment) =>
  atApartment ? [...AT_APARTMENT_CATEGORIES, ...addedAptCategoryNames()] : REGULAR_CATEGORIES

// The category a picked catalogue item contributes to a sheet line, or '' when
// it has nothing usable. The main program-material catalogue keeps its OWN
// taxonomy ("Category Games - Art"), which is not the daily sheet's vocabulary —
// copying it straight onto the line made the API reject the save with "unknown
// category" (2026-07-22). Keep the sheet's own lists authoritative and let the
// person pick when the catalogue's value doesn't belong to one.
export const sheetCategory = (value, atApartment) => {
  const wanted = String(value || '').trim().toLowerCase()
  if (!wanted) return ''
  return (
    categoryOptionsFor(atApartment).find((c) => c.toLowerCase() === wanted) || ''
  )
}

// Starter vendor list (the daily sheet's regulars). The grids add any vendor
// already present in the data, plus an "add new" entry that prompts — so a
// new vendor never needs a deploy.
export const VENDOR_OPTIONS = ['Anmol', 'Amazon']
export const ADD_VENDOR = '__add_vendor__'
// Sentinel value in the vendor dropdown that opens the vendor master (P4):
// vendors are HQ-controlled, so a line can only pick an approved vendor, and
// new ones are proposed/approved in the manager rather than typed inline.
export const MANAGE_VENDORS = '__manage_vendors__'

export const vendorOptionsFrom = (rows) => [
  ...new Set([
    ...VENDOR_OPTIONS,
    ...rows.map((r) => r.vendor).filter(Boolean)
  ])
]

// THE daily-sheet column set, in the sheet's exact order (user 2026-07-17:
// "keep this format intact" — the HQ view maintains the same register).
// Requested by | CENTER(->Apartment) | Vendor | Category | Sl No. |
// Reference ID | Product | File | Print specification | Kind of Paper |
// Sides per file | Units | Total sides | PPP | Taxable | GST | Invoice Amt |
// Approval | Dispatch — plus Unit ₹ (non-print pricing), Link and Source,
// which the sheet never had a home for.
//
// kind: 'text' | 'num' | 'select' | 'link' | 'calc' | 'money' | undefined
// (undefined = display-only). `db` names the API field an edit writes.
export const ITEM_TYPE_OPTIONS = [
  ['goods', 'Goods'],
  ['print', 'Print']
]

// Print-only columns: dead (non-editable, shown as —) on Goods lines (P4,
// 2026-07-18). Goods never carry artwork, print specs, paper or sides.
export const PRINT_ONLY = new Set([
  'file_link',
  'print_specification',
  'paper_kind',
  'sides_per_file',
  'total_sides'
])

// A print-only cell is dead on a goods row.
export const cellDead = (row, col) =>
  PRINT_ONLY.has(col.k) && (row.item_type || 'goods') === 'goods'

// Invoice is normally computed; when the line is GST-inclusive it is the INPUT
// (the known Amazon-style total) and taxable/GST are derived from it.
export const invoiceEditable = (row) => !!row.amount_inclusive

export const SHEET_COLS = [
  { tip: "Line number within its request — restarts per request, exactly like the sheet.", k: 'sl_no', label: 'Sl No.', kind: 'calc' },
  { tip: "The day this line belongs to. New lines take the day you are looking at, so a day that was missed can still be filled in afterwards. Editable.", k: 'sheet_date', label: 'Date', kind: 'date', db: 'sheet_date', width: 120, filter: true },
  { tip: "Tick for an At Apartment line — the Center becomes the apartment and the At Apartment categories apply. Untick for a regular centre line.", k: 'at_apartment', label: 'At Apartment', kind: 'at_apartment', width: 60, filter: true },
  { tip: "The centre for a regular line; for an At Apartment line this shows the apartment.", k: 'centre_label', label: 'Center/Apartment', kind: 'centre', db: 'centre_label', width: 150, filter: true },
  { tip: "Who HQ orders from — pick an HQ-approved vendor. Propose new ones in the vendor manager.", k: 'vendor', label: 'Vendor', kind: 'vendor', db: 'vendor', width: 90, filter: true },
  // `options` is a getter: the list grows when a category is added from Alfred,
  // and `addNew` puts "+ New category…" at the bottom of the dropdown.
  { tip: "The budget head (3-5 / 5+ x subject). Decides which budget the spend hits. Pick “+ New category…” to add one.", k: 'category', label: 'Category', kind: 'select', db: 'category', get options() { return aptCategoryNames() }, addNew: true, filter: true },
  { tip: "Goods or Print. Print lines use the artwork, paper and sides columns and price per side; goods ignore those and price per unit.", k: 'item_type', label: 'Type', kind: 'itemtype', db: 'item_type', width: 80, filter: true },
  { tip: "What is being procured.", k: 'item_name', label: 'Product', kind: 'text', db: 'item_name', width: 160, filter: true },
  { tip: "Free reference — catalogue id, issue-tracker id, anything you use to trace the line.", k: 'reference_id', label: 'Reference ID', kind: 'text', db: 'reference_id', width: 80, filter: true },
  { tip: "One link for the line — the artwork/PDF to print, or the purchase link (Amazon / Drive) for goods. Auto-fills from the catalogue and stays editable.", k: 'reference_link', label: 'File/Link', kind: 'link', db: 'reference_link', width: 110 },
  // `wrap` = the pinned width for the two free-prose columns. A print spec runs
  // to a full sentence, and with every cell nowrap one long value stretched the
  // column to 636px against its declared 150 — the columns after it went off
  // screen. The number is the width the cell is pinned to (width + min + max
  // together: max-width ALONE lets an auto-layout table shrink the column to
  // min-content, which measured 23px). 220/120 measured against the real data:
  // three lines on the longest spec, no column left oversized.
  { tip: "How to print it — colour, sides, binding, perforations… Print lines only.", k: 'print_specification', label: 'Print specification', kind: 'text', db: 'print_specification', width: 150, wrap: 220 },
  { tip: "Paper spec, e.g. A5, 100 GSM. Print lines only.", k: 'paper_kind', label: 'Kind of Paper', kind: 'text', db: 'paper_kind', width: 100, wrap: 120 },
  { tip: "Printed sides per copy. With Units it gives Total sides. Print lines only.", k: 'sides_per_file', label: 'Sides per file', kind: 'num', db: 'sides_per_file', width: 60 },
  { tip: "How many copies / pieces.", k: 'qty', label: 'Units', kind: 'num', db: 'qty', width: 55 },
  { tip: "Sides per file x Units — computed, not typed. Print lines only.", k: 'total_sides', label: 'Total sides', kind: 'calc' },
  { tip: "The rate: price per printed side (Print) or per unit (Goods). Taxable = Total sides x Rate for print, Units x Rate for goods.", k: 'ppp', label: 'Rate', kind: 'num', db: 'ppp', width: 60 },
  { tip: "The taxable value — Total sides x Rate for print, Units x Rate for goods. Computed. (For GST-inclusive lines it is derived from the invoice.)", k: 'line_total', label: 'Taxable', kind: 'money' },
  { tip: "GST rate %. Defaults to 18, editable per line (18 / 12 / 5 / 0).", k: 'gst_rate', label: 'GST %', kind: 'num', db: 'gst_rate', width: 55 },
  { tip: "Taxable + GST — computed from Taxable and the GST rate.", k: 'invoice_amount', label: 'Invoice Amt', kind: 'money' },
  { tip: "The manual sign-off for this request — who approved it, how it came (Slack / WhatsApp / Email / Verbal / Issue tracker) and a reference (subject or tracker id). Click to edit.", k: 'approval', label: 'Approval', kind: 'approval', width: 160 },
  { tip: "How far the ITEMS on this order have actually got — the same nine stages the tracking popup shows: requested -> cluster approved -> final approved -> at Procurement HQ -> sent to vendor -> received by HQ -> handed to At Apartment -> dispatched -> received. Where only some items have reached the stage, it says how many. Click to see the full dated tracking.", k: 'status', label: 'Status', filter: true },
  { tip: "HQ's dispatch state per line, in the same words as the Status tracking: Sent to vendor / Received by HQ procurement team / Delivered. Vendors deliver on different dates, so this is per line. The later stages (Handed over, Dispatched to apartment, Received at apartment) are set at hand-over and in the Receipt column, not here.", k: 'fulfilment_status', label: 'Dispatch', kind: 'dispatch', db: 'fulfilment_status', filter: true },
  { tip: "Where the ask came from — Slack, WhatsApp, email, verbal… so nothing untraceable lands in the register.", k: 'source_of_request', label: 'Source', kind: 'select', db: 'source_of_request', options: SOURCE_OPTIONS, filter: true },
  { tip: "Remark on the line — note a shortfall or discrepancy (short / damaged / wrong) when the goods are received. Editable while the line is being fulfilled.", k: 'remarks', label: 'Remarks', kind: 'text', db: 'remarks', width: 180, filter: true },
  { tip: "Who raised the line — taken from their Alfred login, never typed.", k: 'requested_by_name', label: 'Requested by', filter: true }
]

// ---------------------------------------------------------------- column bands
// Sheets-style collapsible column bands (2026-08-26), ported from PayFlow's
// COL_GROUPS on /payments. Twenty-five columns is more than any single job
// needs, so related runs fold to one narrow stub that stays clickable — a
// folded column leaves a visible scar rather than silently disappearing, which
// on a shared operational sheet would read as missing data.
//
// ⛔ Each band MUST be a CONTIGUOUS run of SHEET_COLS: the band header renders
// with a colSpan over the run, so a gap would mis-align the whole header. If
// the column order changes, re-check these.
// Anything not in a band is always visible.
export const COL_BANDS = [
  {
    id: 'refs',
    label: 'Reference',
    short: 'REF',
    cols: ['reference_id', 'reference_link']
  },
  {
    id: 'print',
    label: 'Print detail',
    short: 'PRINT',
    cols: ['print_specification', 'paper_kind', 'sides_per_file']
  },
  {
    // Approval sits here rather than on its own: it is the money sign-off, it
    // is contiguous with the money run, and receiving does not need it either.
    //
    // ⭐ `total_sides` leads the run (2026-09-02). It was in NO band, and an
    // unbanded column is ALWAYS visible — so on both registers it could not be
    // hidden. It must be THIS band and not Print detail: a band is a CONTIGUOUS
    // run and the order is `… sides_per_file, qty, total_sides, ppp …`, so `qty`
    // separates it from the print run while it sits flush against the money one.
    // ⛔ `qty`/Units stays outside every band — it is always visible.
    id: 'money',
    label: 'Money & approval',
    short: '₹',
    cols: [
      'total_sides',
      'ppp',
      'line_total',
      'gst_rate',
      'invoice_amount',
      'approval'
    ]
  },
  {
    id: 'trace',
    label: 'Provenance',
    short: 'TRACE',
    cols: ['source_of_request', 'remarks', 'requested_by_name']
  }
]

export const BAND_OF = {}
COL_BANDS.forEach((b) => {
  b.cols.forEach((k) => {
    BAND_OF[k] = b
  })
})

// Named column sets for the jobs actually done on this sheet. A view just sets
// which bands are folded, so touching any band afterwards simply drops you to
// a custom arrangement — the view is a starting point, not a cage.
export const COL_VIEWS = [
  { id: 'all', label: 'Everything', fold: [] },
  { id: 'ordering', label: 'Ordering', fold: ['trace'] },
  { id: 'receiving', label: 'Receiving', fold: ['refs', 'print', 'money'] },
  { id: 'finance', label: 'Finance', fold: ['print', 'trace'] }
]

// Columns frozen to the left edge under horizontal scroll: Sl No. holds the
// left edge, and Product docks beside it the moment it reaches there.
//
// ⛔ THE INVARIANT IS `LEAD_WIDTH = 0`, NOT CONTIGUITY. This first shipped with
// a 66px lead offset, which left a 66px window between the sidebar and the
// frozen Sl No. cell — the scrolling columns showed through it and "3-5
// language" rendered as "3-. 15 ge". That gap was the bug. Freezing two
// columns that are far apart in the sheet is fine: everything between them
// scrolls underneath and is hidden once Product docks, and before it docks
// those columns are simply in normal flow.
//
// ⛔ Deliberately NOT centre_label: every At Apartment line carries the same
// centre, so freezing it would spend 150px saying nothing.
export const PINNED_COLS = ['sl_no', 'item_name']

// Width each frozen column is held to, so the cumulative left offsets are
// deterministic instead of depending on what the browser measures.
export const PINNED_WIDTH = { sl_no: 54, item_name: 190 }

// ⛔ MUST BE 0. Anything else opens a window between the sidebar and the first
// frozen column, and the scrolled content shows through it.
export const LEAD_WIDTH = 0

export const NUM_FIELDS = new Set(['qty', 'sides_per_file', 'ppp', 'gst_rate'])

// The per-line dispatch states HQ can set from the grid. The labels match the
// Status tracking vocabulary so the dropdown and the timeline read the same
// (2026-07-25, P2): `ordered` = Sent to vendor, `dispatched` = Received by HQ procurement team,
// `delivered` = the line is done (what kit-completeness counts). The underlying
// enum values are unchanged — only the labels are aligned.
export const DISPATCH_OPTIONS = [
  ['pending', 'Pending'],
  ['ordered', 'Sent to vendor'],
  ['dispatched', 'Received by HQ procurement team'],
  ['delivered', 'Delivered / done'],
  ['unavailable', 'Unavailable']
]

// The tail of the journey, shown greyed and non-clickable in the dispatch
// dropdown so the full status list reads consistently — but these are set
// elsewhere (hand-over action + the Receipt column), never from this cell.
export const DISPATCH_DOWNSTREAM = [
  'Handed over',
  'Dispatched to apartment',
  'Received at apartment'
]

// value -> label lookup for the display (non-editing) cell, so the chip reads
// the same words as the dropdown instead of the raw enum value.
export const DISPATCH_LABEL = Object.fromEntries(DISPATCH_OPTIONS)

export const matchesFilter = (value, f) =>
  !f ||
  String(value || '')
    .toLowerCase()
    .includes(f.trim().toLowerCase())

/** Flatten request objects into sheet rows (adds sl_no within each request). */
export const toSheetRows = (requests) =>
  requests.flatMap((r) =>
    (r.lines || []).map((line, i) => ({
      ...line,
      sl_no: i + 1,
      request: r,
      apartment_name: r.apartment_name,
      requested_by_name: r.requested_by_name,
      po_number: r.po_number || '',
      status: r.status
    }))
  )
